import { GetSecretValueCommand, GetSecretValueResponse, SecretsManagerClient, SecretsManagerClientConfig } from '@aws-sdk/client-secrets-manager';
import ENVIRONMENT_VARIABLES from 'src/constants/environment-variables';
import { eSecretManagerKeys } from 'src/constants/generic-constants';
import { getAWSServiceCredentials } from './auth-token-services';
import { retrieveParameterFromSSM } from './ssm-parameter';

/**
 * Returns the Amplify authentication configuration.
 * @returns The Amplify authentication configuration.
 */
export const getAmplifyAuthConfig = () => {
  return {
    Auth: {
      Cognito: {
        region: ENVIRONMENT_VARIABLES.env.Region,
        userPoolId: ENVIRONMENT_VARIABLES.env.CognitoUserPoolId,
        userPoolClientId: ENVIRONMENT_VARIABLES.env.CognitoUserPoolWebClientId,
        identityPoolId: ENVIRONMENT_VARIABLES.env.CognitoIdentityPoolId,
        awsAccountId: ENVIRONMENT_VARIABLES.env.AwsAccountId,
        mandatorySignIn: true,
        authenticationFlowType: 'USER_SRP_AUTH',
        loginWith: {
          oauth: {
            domain: ENVIRONMENT_VARIABLES.env.CognitoDomain,
            scopes: ['openid'],
            redirectSignIn: [ENVIRONMENT_VARIABLES.env.ApplicationDomain],
            redirectSignOut: [ENVIRONMENT_VARIABLES.env.ApplicationDomain],
            responseType: 'code' as const
          }
        }
      }
    }
  };
};

// Applicable to only for Dev environment (dev).
const APP_SYNC_ENDPOINT_SECRET_FOR_DEV = 'arn:aws:secretsmanager:us-west-2:631849255022:secret:xpt-app-api-secret-dev-qo2qX7';

// Applicable to all other environments (alpha, beta & prod)
const APP_SYNC_ENDPOINT_PARAMETER = '/xpt-fe/xpt-appsync-endpoint';

// Events API Configuration - Full ARNs for Secrets Manager per stage
const EVENTS_API_SECRET_ARNS = {
  dev: 'arn:aws:secretsmanager:us-west-2:828750669660:secret:DaSFinGenAISummarizer-EventsAPI-v2-Secret-alpha-P7Ofvz',
  alpha: 'arn:aws:secretsmanager:us-west-2:828750669660:secret:DaSFinGenAISummarizer-EventsAPI-v2-Secret-alpha-P7Ofvz',
  beta: 'arn:aws:secretsmanager:us-west-2:209815669440:secret:DaSFinGenAISummarizer-EventsAPI-v2-Secret-beta-nIPrWD',
  prod: 'arn:aws:secretsmanager:us-west-2:499591337244:secret:DaSFinGenAISummarizer-EventsAPI-v2-Secret-prod-5ClSEZ'
};

const EVENTS_API_SECRET_REGION = 'us-west-2';

/**
 * Retrieves the AppSync endpoint information for the development environment.
 * @returns A Promise that resolves to the AppSync endpoint information or undefined if not found.
 */
export const retrieveDevAppSyncEndpointInfo = async (): Promise<GetSecretValueResponse> => {
  const secretsManageClientConfig: SecretsManagerClientConfig = {
    region: ENVIRONMENT_VARIABLES.env.Region,
    credentials: await getAWSServiceCredentials()
  };

  const secretsManagerClient = new SecretsManagerClient(secretsManageClientConfig);
  const command = new GetSecretValueCommand({ SecretId: APP_SYNC_ENDPOINT_SECRET_FOR_DEV });

  return await secretsManagerClient.send(command);
};

/**
 * Retrieves the Events API configuration from a different AWS account's Secrets Manager.
 * Uses full ARN for explicit cross-account access.
 * @param stage The current environment stage (dev, alpha, beta, prod)
 * @returns A Promise that resolves to an object containing the API key and endpoint.
 */
export const retrieveEventsAPIConfig = async (stage: string): Promise<{ apiKey: string; endpoint: string }> => {
  // Get the full ARN for the appropriate stage
  const secretARN = EVENTS_API_SECRET_ARNS[stage as keyof typeof EVENTS_API_SECRET_ARNS] || EVENTS_API_SECRET_ARNS.alpha;

  console.debug('Fetching Events API secret:', {
    stage,
    secretARN,
    region: EVENTS_API_SECRET_REGION
  });

  const secretsManageClientConfig: SecretsManagerClientConfig = {
    region: EVENTS_API_SECRET_REGION,
    credentials: await getAWSServiceCredentials()
  };

  const secretsManagerClient = new SecretsManagerClient(secretsManageClientConfig);
  // Use full ARN for explicit cross-account access
  const command = new GetSecretValueCommand({ SecretId: secretARN });

  const response = await secretsManagerClient.send(command);

  if (response.SecretString) {
    const parsedSecrets = JSON.parse(response.SecretString);
    return {
      apiKey: parsedSecrets[eSecretManagerKeys.EVENTS_API_KEY] || '',
      endpoint: parsedSecrets[eSecretManagerKeys.EVENTS_API_ENDPOINT] || ''
    };
  }

  return { apiKey: '', endpoint: '' };
};

/**
 * Retrieves the AppSync configuration based on the environment stage.
 * Includes both GraphQL and Events API configurations.
 * All stages (including dev) use Cognito authentication for GraphQL API.
 * @returns A Promise that resolves to the AppSync configuration.
 */
export const getAppSyncConfig = async () => {
  const { Stage, Region } = ENVIRONMENT_VARIABLES.env;

  try {
    let graphQlAPI: string = '';
    let eventsAPIConfig: { apiKey: string; endpoint: string };

    // TODO(XPT): Re-introduce the dev-stage Secrets Manager path once the Dev API
    // is ready. For now all stages (alpha, beta, prod) resolve the AppSync
    // endpoint from SSM Parameter Store.
    graphQlAPI = await retrieveParameterFromSSM(APP_SYNC_ENDPOINT_PARAMETER);

    // Retrieve Events API configuration from different AWS account's Secrets Manager
    // This includes both the API key and endpoint from the same secret
    // Make this optional to not block app initialization if it fails
    try {
      eventsAPIConfig = await retrieveEventsAPIConfig(Stage);
      console.debug('Events API Config successfully retrieved:', {
        stage: Stage,
        endpoint: eventsAPIConfig.endpoint,
        hasApiKey: !!eventsAPIConfig.apiKey,
        apiKeyLength: eventsAPIConfig.apiKey?.length
      });
    } catch (eventsApiError) {
      console.warn(`Unable to retrieve Events API configuration for stage '${Stage}'. Events API features will be disabled:`, eventsApiError);
      eventsAPIConfig = { apiKey: '', endpoint: '' };
    }

    // Events API configuration - use API key authentication for Events API
    const eventsAuthMode = 'apiKey';

    // Build config object - all stages use Cognito (userPool) auth for GraphQL
    const config: any = {
      API: {
        GraphQL: {
          endpoint: graphQlAPI,
          region: Region,
          defaultAuthMode: 'userPool'
        }
      }
    };

    // Only add Events API configuration if we successfully retrieved it
    if (eventsAPIConfig.endpoint && eventsAPIConfig.apiKey) {
      config.API.Events = {
        endpoint: eventsAPIConfig.endpoint,
        region: EVENTS_API_SECRET_REGION,
        defaultAuthMode: eventsAuthMode,
        apiKey: eventsAPIConfig.apiKey
      };
    }

    return config;
  } catch (error) {
    console.error('Unable to retrieve AppSync configuration:', error);
    throw error;
  }
};

/**
 * Returns the PAPI endpoint. All environments use the production endpoint.
 */
const getApiEndpoint = (): string => {
  return 'https://fintech-papi.device.finance.amazon.dev';
};

/**
 * Retrieves the PAPI configuration based on the environment stage.
 * @returns An object containing the PAPI configuration.
 */
export const getApiConfig = () => {
  const apiEndpoint = getApiEndpoint();

  return {
    API: {
      REST: {
        PAPI: {
          endpoint: apiEndpoint,
          region: 'us-west-2' // PAPI region is in us-west-2
        }
      }
    }
  };
};
